import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { agriculturalAPI, chartsAPI } from '../services/api';
import {
  Globe,
  BarChart3,
  ArrowRight,
  DollarSign,
  Activity,
  Sparkles,
  TrendingUp,
  LineChart,
  Search,
  Zap,
  Layers,
  CheckCircle2,
  Database,
  Filter,
  Brain,
  Users,
  Target,
  Shield,
  Play,
  ChevronRight,
  FileText,
  BarChart as BarChartIcon,
  PieChart as PieChartIcon,
  LineChart as LineChartIcon,
  Bookmark,
  Eye,
  Loader2,
  AreaChart as AreaChartIcon,
  Calendar,
  Tag,
  Table,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  LineChart as RechartsLineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  ScatterChart,
  Scatter,
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';

const AnalyticsHome: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const [mounted, setMounted] = useState(false);

  const handleNavigate = (route: string) => {
    if (!user) {
      if (route === '/search') {
        navigate('/search');
        return;
      }
      navigate('/login');
      return;
    }
    navigate(route);
  };
  const [activeTab, setActiveTab] = useState('Explore');
  const [elasticsearchStats, setElasticsearchStats] = useState({
    totalDocuments: 0,
    totalIndices: 0,
    totalCharts: 0,
  });
  const [hasCharts, setHasCharts] = useState(false);
  const [loadingCharts, setLoadingCharts] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Array<{ x: number; y: number; vx: number; vy: number }>>([]);
  const animationFrameRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    setMounted(true);
    if (user) {
      loadElasticsearchStats();
      loadSavedCharts();
    }
  }, [user]);

  const loadSavedCharts = async () => {
    try {
      setLoadingCharts(true);
      const response = await chartsAPI.getCharts(true);
      if (response.data.success && response.data.data) {
        const charts = response.data.data || [];
        setHasCharts(charts.length > 0);
      }
    } catch (err: any) {
      console.error('Error loading saved charts:', err);
      setHasCharts(false);
    } finally {
      setLoadingCharts(false);
    }
  };

  const loadElasticsearchStats = async () => {
    try {
      // Charger les indices Elasticsearch
      const indicesResponse = await agriculturalAPI.getIndices();
      let totalDocs = 0;
      let totalIndices = 0;
      
      if (indicesResponse.data.success && indicesResponse.data.data) {
        const indices = indicesResponse.data.data;
        totalDocs = indices.reduce((sum: number, idx: any) => sum + (idx.documentCount || 0), 0);
        totalIndices = indices.length;
      }

      // Charger le nombre de graphiques créés
      let totalCharts = 0;
      try {
        const chartsResponse = await chartsAPI.getCharts(true); // inclure les graphiques publics
        if (chartsResponse.data.success && chartsResponse.data.data) {
          totalCharts = chartsResponse.data.data.length || 0;
        }
      } catch (chartsError) {
        console.warn('Could not load charts count:', chartsError);
      }

      setElasticsearchStats({
        totalDocuments: totalDocs,
        totalIndices: totalIndices,
        totalCharts: totalCharts,
      });
    } catch (error) {
      console.error('Error loading Elasticsearch stats:', error);
      // Valeurs par défaut en cas d'erreur
      setElasticsearchStats({
        totalDocuments: 0,
        totalIndices: 0,
        totalCharts: 0,
      });
    }
  };

  // Particle animation system
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Initialize particles
    const particleCount = 60;
    particlesRef.current = Array.from({ length: particleCount }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.5,
      vy: (Math.random() - 0.5) * 0.5,
    }));

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Update and draw particles
      particlesRef.current.forEach((particle, i) => {
        // Update position
        particle.x += particle.vx;
        particle.y += particle.vy;

        // Bounce off edges
        if (particle.x < 0 || particle.x > canvas.width) particle.vx *= -1;
        if (particle.y < 0 || particle.y > canvas.height) particle.vy *= -1;

        // Keep particles in bounds
        particle.x = Math.max(0, Math.min(canvas.width, particle.x));
        particle.y = Math.max(0, Math.min(canvas.height, particle.y));

        // Draw particle
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, 2, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(16, 185, 129, 0.6)'; // emerald-500
        ctx.fill();

        // Draw connections
        particlesRef.current.slice(i + 1).forEach((other) => {
          const dx = particle.x - other.x;
          const dy = particle.y - other.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 150) {
            ctx.beginPath();
            ctx.moveTo(particle.x, particle.y);
            ctx.lineTo(other.x, other.y);
            ctx.strokeStyle = `rgba(16, 185, 129, ${0.2 * (1 - distance / 150)})`;
            ctx.lineWidth = 1;
            ctx.stroke();
          }
        });
      });

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  // Generate animated chart data
  const generateAreaData = () => {
    return Array.from({ length: 20 }, (_, i) => ({
      x: i,
      value: Math.sin(i * 0.5) * 50 + 100 + Math.random() * 30,
    }));
  };

  const generateBarData = () => {
    return Array.from({ length: 8 }, (_, i) => ({
      name: String.fromCharCode(65 + i),
      value: Math.random() * 100 + 50,
    }));
  };

  const generateCandlestickData = () => {
    return Array.from({ length: 10 }, (_, i) => ({
      x: i,
      open: 80 + Math.random() * 40,
      close: 80 + Math.random() * 40,
      high: 100 + Math.random() * 40,
      low: 60 + Math.random() * 40,
    }));
  };

  const generateDonutData = () => {
    return [
      { name: 'A', value: 35, color: '#3b82f6' },
      { name: 'B', value: 25, color: '#10b981' },
      { name: 'C', value: 20, color: '#8b5cf6' },
      { name: 'D', value: 20, color: '#f59e0b' },
    ];
  };

  const generateScatterData = () => {
    return Array.from({ length: 30 }, () => ({
      x: Math.random() * 100,
      y: Math.random() * 100,
    }));
  };

  // Mock chart data for preview on hover
  const getChartData = (type: string) => {
    const baseData = Array.from({ length: 12 }, (_, i) => ({
      month: i + 1,
      value: Math.random() * 100 + 50 + Math.sin(i) * 20,
    }));
    
    if (type === 'production') {
      return baseData.map(d => ({ ...d, value: d.value * 1.2 }));
    } else if (type === 'prices') {
      return baseData.map(d => ({ ...d, value: d.value * 0.8 }));
    } else {
      return baseData.map(d => ({ ...d, value: d.value * 1.5 }));
    }
  };

  const analyticsPages = [
    {
      title: 'Data Explorer',
      description: 'Advanced search and visualization tool with dynamic chart creation, multi-filter analysis, and intelligent data exploration',
      icon: Search,
      route: '/search',
      gradient: 'bg-gradient-to-br from-amber-500 to-orange-600',
      bgColor: 'bg-amber-50',
      iconColor: 'text-amber-600',
      features: ['Dynamic Charts', 'Multi-Filter Analysis', 'Intelligent Series Detection', 'Multiple Chart Types'],
      chartType: 'production',
      chartColor: '#f59e0b',
      isNew: true,
      badge: 'Power Tool',
    },
    {
      title: 'Production Data',
      description: 'Analyze global agricultural production with interactive visualizations',
      icon: BarChart3,
      route: '/visualization',
      gradient: 'bg-gradient-to-br from-blue-500 to-blue-600',
      bgColor: 'bg-blue-50',
      iconColor: 'text-blue-600',
      features: ['Interactive Maps', 'Time Series Charts', 'Country Comparisons'],
      chartType: 'production',
      chartColor: '#3b82f6',
    },
    {
      title: 'Producer Prices',
      description: 'Track producer prices across commodities and regions',
      icon: DollarSign,
      route: '/producer-prices',
      gradient: 'bg-gradient-to-br from-emerald-500 to-emerald-600',
      bgColor: 'bg-emerald-50',
      iconColor: 'text-emerald-600',
      features: ['Price Trends', 'Regional Analysis', 'Monthly Data'],
      chartType: 'prices',
      chartColor: '#10b981',
    },
    {
      title: 'Trade Analytics',
      description: 'Explore import and export flows with comprehensive trade metrics',
      icon: Activity,
      route: '/trade-analytics',
      gradient: 'bg-gradient-to-br from-purple-500 to-purple-600',
      bgColor: 'bg-purple-50',
      iconColor: 'text-purple-600',
      features: ['Trade Balance', 'Flow Analysis', 'Year-over-Year Growth'],
      chartType: 'trade',
      chartColor: '#8b5cf6',
    },
  ];

  const tabContent = {
    Explore: {
      title: 'Explore: Intelligent Data Discovery',
      description: 'Connect to any data index with advanced search capabilities. Navigate through millions of records with intelligent field detection, automatic type recognition, and smart suggestions. Our platform understands your data structure and helps you find exactly what you need.',
      image: '/analytics.PNG',
      buttons: ['Advanced Search', 'Field Detection'],
    },
    Filter: {
      title: 'Filter: Advanced Multi-Filter System',
      description: 'Apply multiple filters simultaneously across any field. Our intelligent filter system automatically detects field types and suggests relevant values, making complex queries simple. Filter by date ranges, categories, numerical values, and more with real-time suggestions and validation.',
      image: '/filter.PNG',
      buttons: ['Multi-Value Selection', 'Real-time Suggestions'],
    },
    Visualize: {
      title: 'Visualize: Dynamic Chart Generation',
      description: 'Create multiple chart types from your filtered data. Automatically generate separate visualizations for each combination of filters, enabling comprehensive comparative analysis. Choose from bar charts, line charts, pie charts, scatter plots, and more with intelligent axis detection.',
      image: '/visualize.PNG',
      buttons: ['Chart Types', 'Multi-Chart Generation'],
    },
    Analyze: {
      title: 'Analyze: Intelligent Data Analysis',
      description: 'Perform deep data analysis with automatic pattern detection and statistical insights. Our platform analyzes your data structure, detects relationships, and suggests optimal analysis methods. Get comprehensive statistics, trends, correlations, and predictive insights.',
      image: '/analyze.PNG',
      buttons: ['Pattern Detection', 'Statistical Insights'],
    },
    Export: {
      title: 'Export: Share & Integrate',
      description: 'Export your filtered results and visualizations in multiple formats. Share insights with your team or integrate with other tools seamlessly. Export as CSV, JSON, images, or shareable links with customizable formatting and metadata.',
      image: '/export.PNG',
      buttons: ['CSV/JSON Export', 'Shareable Links'],
    },
  };

  const areaData1 = generateAreaData();
  const areaData2 = generateAreaData();
  const areaData3 = generateAreaData();
  const barData = generateBarData();
  const candlestickData = generateCandlestickData();
  const donutData = generateDonutData();
  const scatterData = generateScatterData();

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Particle Canvas Background */}
      <canvas
        ref={canvasRef}
        className="fixed inset-0 pointer-events-none z-0 opacity-20"
        style={{ background: 'transparent' }}
      />

      {/* Main Content */}
      <div className="relative z-10">
        {/* Hero Section */}
        <section id="hero" className="relative min-h-screen flex items-center overflow-hidden">
          {/* Background Image */}
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `url('https://www.cropin.com/wp-content/uploads/2024/08/The-role-of-data-analytics-in-modern-agriculture.jpg')`,
            }}
          >
            {/* Dark Overlay pour améliorer la lisibilité du texte */}
            <div className="absolute inset-0 bg-gradient-to-r from-gray-900/70 via-gray-900/60 to-gray-900/50"></div>
          </div>

          {/* Content */}
          <div className="relative z-10 w-full py-32">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-3xl">
                {/* Text Content */}
                <div className={`space-y-6 ${mounted ? 'animate-fade-in-up' : 'opacity-0'}`}>
                  {/* Badge */}
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100/90 backdrop-blur-sm border border-emerald-200 rounded-full text-emerald-700 text-sm font-medium shadow-sm">
                    <Zap className="w-4 h-4 text-emerald-600" />
                    <span>Complete Analytics Platform</span>
                  </div>

                  {/* Main Title */}
                  <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight">
                    Your{' '}
                    <span className="bg-gradient-to-r from-emerald-400 via-green-400 to-teal-400 bg-clip-text text-transparent">
                      Analytics Platform
                    </span>
                  </h2>

                  {/* Subtitle */}
                  <p className="text-lg md:text-xl text-gray-100 leading-relaxed max-w-2xl">
                    Turn raw data into actionable insights with our complete analytics suite. 
                    Explore, visualize, and understand your agricultural data like never before.
                  </p>

                  {/* CTA Buttons */}
                  <div className="flex flex-col sm:flex-row gap-4 pt-4">
                    <button
                      onClick={() => handleNavigate('/search')}
                      className="group relative px-8 py-4 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-lg font-semibold shadow-md hover:shadow-lg transform hover:scale-[1.02] transition-all duration-300 flex items-center gap-2"
                    >
                      <span className="relative z-10 flex items-center gap-2">
                        <Search className="w-5 h-5" />
                        Explore Data
                        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </button>
                    <button
                      onClick={() => handleNavigate('/visualization')}
                      className="group px-8 py-4 bg-white/10 backdrop-blur-sm border-2 border-white/30 text-white rounded-lg font-semibold hover:bg-white/20 hover:border-white/40 transition-all flex items-center gap-2 shadow-sm"
                    >
                      <BarChart3 className="w-5 h-5" />
                      <span>View Dashboards</span>
                    </button>
                  </div>
      
                  {/* Trust Indicators */}
                  <div className="flex flex-wrap gap-4 pt-4">
                    {['Real-Time Analytics', 'Advanced Visualizations', 'Enterprise-Ready'].map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-white bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-full border border-white/30 shadow-sm">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                        <span className="text-sm font-medium">{item}</span>
                      </div>
                    ))}
                  </div>

                  {/* Statistics - Simple Numbers */}
                  <div className={`flex flex-wrap items-center gap-8 pt-8 ${mounted ? 'animate-fade-in-up animation-delay-500' : 'opacity-0'}`}>
                    <div className="flex items-baseline gap-2">
                      <span className="text-4xl md:text-5xl font-bold text-white">
                        {elasticsearchStats.totalDocuments.toLocaleString()}
                      </span>
                      <span className="text-sm text-gray-300">Documents</span>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-4xl md:text-5xl font-bold text-white">
                        {elasticsearchStats.totalIndices}
                      </span>
                      <span className="text-sm text-gray-300">Indices</span>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-4xl md:text-5xl font-bold text-white">
                        {elasticsearchStats.totalCharts.toLocaleString()}
                      </span>
                      <span className="text-sm text-gray-300">Graphiques</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Scroll Indicator */}
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 animate-bounce">
            <ChevronRight className="w-6 h-6 text-emerald-400/80 rotate-90" />
          </div>
        </section>

        {/* What We Do Section */}
        <section className="py-20 md:py-32 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <span className="inline-block text-emerald-600 font-semibold text-sm uppercase tracking-wider mb-4">WHAT WE DO</span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                Powerful data exploration and analytics platform for researchers and businesses
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
                Whether you are conducting agricultural research, analysing market trends, or building data-driven insights, 
                we enable you to explore, filter, visualize, and understand your data seamlessly with intelligent tools.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
                <button
                  onClick={() => handleNavigate('/search')}
                  className="px-6 py-3 border-2 border-emerald-300 text-emerald-700 rounded-lg font-semibold hover:border-emerald-400 hover:bg-emerald-50 transition-all"
                >
                  Learn more
                </button>
                <button
                  onClick={() => handleNavigate('/search')}
                  className="px-6 py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-emerald-500/30 transition-all flex items-center gap-2"
                >
                  <Play className="w-4 h-4" />
                  View demos
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Platform Capabilities Tabs */}
        <section className="py-20 md:py-32 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap justify-center gap-4 border-b border-gray-200 pb-4 mb-8">
              {['Explore', 'Filter', 'Visualize', 'Analyze', 'Export'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-6 py-3 text-lg font-semibold transition-all ${
                    activeTab === tab
                      ? 'text-emerald-600 border-b-2 border-emerald-600'
                      : 'text-gray-600 hover:text-emerald-600'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>

            {/* Active Tab Content */}
            {tabContent[activeTab as keyof typeof tabContent] && (
              <div className="grid md:grid-cols-2 gap-12 items-center">
                <div className="relative">
                  <div className="aspect-video rounded-xl overflow-hidden border border-gray-200 shadow-lg">
                    <img
                      src={tabContent[activeTab as keyof typeof tabContent].image}
                      alt={tabContent[activeTab as keyof typeof tabContent].title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                <div>
                  <h3 className="text-3xl font-bold text-emerald-600 mb-4">
                    {tabContent[activeTab as keyof typeof tabContent].title}
                  </h3>
                  <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                    {tabContent[activeTab as keyof typeof tabContent].description}
                  </p>
                  <div className="flex flex-wrap gap-3 mb-4">
                    {tabContent[activeTab as keyof typeof tabContent].buttons.map((btn, idx) => (
                      <button
                        key={idx}
                        className="px-4 py-2 border border-emerald-300 text-emerald-700 rounded-lg hover:border-emerald-400 hover:bg-emerald-50 transition-all text-sm"
                      >
                        {btn}
                      </button>
                    ))}
                  </div>
                  <button className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-all text-sm">
                    <Play className="w-4 h-4" />
                    <span>View Overview</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* How We Help Section */}
        <section className="py-20 md:py-32 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                How We Help Researchers & Analysts
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Our platform helps researchers, data analysts, and decision-makers extract meaningful insights 
                from complex agricultural and economic datasets.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-lg hover:shadow-xl transition-all">
                <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-6">
                  <Users className="w-6 h-6 text-emerald-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">For Researchers</h3>
                <p className="text-gray-600 mb-4">
                  Accelerate your research with powerful filtering and visualization tools. Explore large datasets quickly, 
                  identify patterns, and generate publication-ready charts.
                </p>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li>• Fast data exploration</li>
                  <li>• Pattern detection</li>
                  <li>• Comparative analysis</li>
                  <li>• Export for publications</li>
                </ul>
              </div>

              <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-lg hover:shadow-xl transition-all">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-6">
                  <Target className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">For Data Analysts</h3>
                <p className="text-gray-600 mb-4">
                  Streamline your analytics workflow with intelligent field detection and automatic chart generation. 
                  Focus on insights, not on configuration.
                </p>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li>• Automated insights</li>
                  <li>• Multi-dimensional analysis</li>
                  <li>• Real-time filtering</li>
                  <li>• Collaborative sharing</li>
                </ul>
              </div>

              <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-lg hover:shadow-xl transition-all">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-6">
                  <TrendingUp className="w-6 h-6 text-teal-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">For Decision-Makers</h3>
                <p className="text-gray-600 mb-4">
                  Make confident data-driven decisions. Access comprehensive visualizations and insights 
                  that help you understand trends and make informed choices.
                </p>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li>• Executive dashboards</li>
                  <li>• Trend analysis</li>
                  <li>• Impact evaluation</li>
                  <li>• Strategic planning support</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Analytics Pages Section */}
        <section className="py-20 md:py-32 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                Explorez Nos Outils
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Accédez à des outils d'analytics spécialisés pour différents domaines de données
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {analyticsPages.map((page, index) => {
                const IconComponent = page.icon;
                const chartData = getChartData(page.chartType);
                
                // Update colors to match emerald/green theme
                const colorMap: { [key: string]: { bg: string; icon: string; gradient: string; chart: string } } = {
                  'from-amber-500 to-orange-600': { 
                    bg: 'bg-emerald-50', 
                    icon: 'text-emerald-600', 
                    gradient: 'from-emerald-500 to-emerald-600',
                    chart: '#10b981'
                  },
                  'from-blue-500 to-blue-600': { 
                    bg: 'bg-green-50', 
                    icon: 'text-green-600', 
                    gradient: 'from-green-500 to-green-600',
                    chart: '#16a34a'
                  },
                  'from-emerald-500 to-emerald-600': { 
                    bg: 'bg-teal-50', 
                    icon: 'text-teal-600', 
                    gradient: 'from-teal-500 to-teal-600',
                    chart: '#14b8a6'
                  },
                  'from-purple-500 to-purple-600': { 
                    bg: 'bg-emerald-50', 
                    icon: 'text-emerald-600', 
                    gradient: 'from-emerald-500 to-emerald-600',
                    chart: '#10b981'
                  },
                };
                
                const colors = colorMap[page.gradient] || { 
                  bg: 'bg-emerald-50', 
                  icon: 'text-emerald-600', 
                  gradient: 'from-emerald-500 to-emerald-600',
                  chart: '#10b981'
                };
                
                return (
                  <div
                    key={page.title}
                    onClick={() => handleNavigate(page.route)}
                    className={`group relative bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden border border-gray-200 hover:border-emerald-300 cursor-pointer ${mounted ? `animate-fade-in-up animation-delay-${index * 200}` : 'opacity-0'}`}
                  >
                    <div className="relative p-6">
                      {/* Badge for new/power tools */}
                      {page.isNew && (
                        <div className="absolute top-4 right-4 px-3 py-1 bg-gradient-to-r from-emerald-400 to-emerald-500 text-white text-xs font-bold rounded-full shadow-lg z-10 flex items-center gap-1">
                          <Zap className="w-3 h-3" />
                          {page.badge}
                        </div>
                      )}
                      
                      {/* Icon */}
                      <div className={`inline-flex items-center justify-center w-14 h-14 rounded-xl ${colors.bg} mb-5 group-hover:scale-110 transition-transform duration-300`}>
                        <IconComponent className={`h-7 w-7 ${colors.icon}`} />
                      </div>

                      {/* Content */}
                      <h3 className="text-lg font-bold text-gray-900 mb-2 transition-colors group-hover:text-emerald-600">
                        {page.title}
                      </h3>
                      <p className="text-gray-600 text-sm mb-4 line-clamp-3 leading-relaxed">
                        {page.description}
                      </p>

                      {/* Features List */}
                      <ul className="space-y-1.5 mb-4">
                        {page.features.map((feature, idx) => (
                          <li key={idx} className="text-xs text-gray-600 flex items-center gap-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
                            {feature}
                          </li>
                        ))}
                      </ul>

                      {/* Call to Action */}
                      <div className="flex items-center font-semibold mt-4 text-emerald-600">
                        <span className="text-sm mr-2">Explorer</span>
                        <ArrowRight className="h-4 w-4 group-hover:translate-x-2 transition-transform duration-300" />
                      </div>
                    </div>

                    {/* Bottom Border Effect */}
                    <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${colors.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300`}></div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      </div>


      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=Inter:wght@300;400;500;600;700&display=swap');
        
        @keyframes blob {
          0%, 100% {
            transform: translate(0, 0) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes gradient {
          0%, 100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }

        @keyframes pulse {
          0%, 100% {
            opacity: 0.8;
          }
          50% {
            opacity: 1;
          }
        }

        .animate-blob {
          animation: blob 7s infinite;
        }

        .animation-delay-2000 {
          animation-delay: 2s;
        }

        .animation-delay-4000 {
          animation-delay: 4s;
        }

        .animate-fade-in-up {
          animation: fadeInUp 0.6s ease-out forwards;
        }

        .animate-gradient {
          background-size: 200% 200%;
          animation: gradient 3s ease infinite;
        }

        .animation-delay-100 {
          animation-delay: 0.1s;
        }

        .animation-delay-200 {
          animation-delay: 0.2s;
        }

        .animation-delay-300 {
          animation-delay: 0.3s;
        }

        .animation-delay-400 {
          animation-delay: 0.4s;
        }

        .animation-delay-500 {
          animation-delay: 0.5s;
        }

        .animation-delay-600 {
          animation-delay: 0.6s;
        }

        /* Smooth scrolling */
        html {
          scroll-behavior: smooth;
        }
      `}</style>
    </div>
  );
};

export default AnalyticsHome;
